// ---------------------------------------------------------------------------
// B5 — **THE MOSES WORD MAP. CC PROPOSES WITH THE PASSAGE, SHE APPROVES PER MAPPING.**
//
// Her order: *for each failing aim-word, pull the candidate seals from her own books WITH the
// passage citations that justify each match, and deliver the proposal as an APPROVAL LIST on her
// dashboard (approve / edit / skip per mapping, the review-ledger pattern). NOTHING ships into
// delivery without her tap per mapping; an honest no-match stays until she approves.*
//
// >>> **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND THE MEASUREMENT DECIDED THE SHAPE.** <<<
// Driven over the 60 distinct failing asks in `gate_log` under `moses-no-match`: **every one binds
// NOTHING today**, and her books' own TEXT justifies almost none of them — searching the 19 entries'
// purpose sentences and conjurations for the asks' own words returns **3 hits out of 32 terms, and
// all three are noise** (*"table of employment"* matching the word *table*; *"learn to bake
// sourdough bread"* matching *learn* in a seal about dreams and visions). Her Seventh Book's twelve
// tables are about treasure, fortune, suits at law, quarrels, honour, love, dreams and chemistry;
// her members are asking about hex-breaking, uncrossing, employment, exams and self-esteem.
//
// **SO A PROPOSAL IS A JUDGEMENT WITH A CITATION ATTACHED, NEVER A TEXT MATCH — AND THAT IS EXACTLY
// WHY IT NEEDS HER TAP.** N13's standing ruling on this surface is *never the nearest thing*, and
// MAGIC-MATCH-1 refused to widen the matchers for that reason. **What that ruling forbids is
// SHIPPING a guess; an approval list is its opposite.** Every proposal carries the entry's OWN
// sentence, verbatim, so she is judging her own book's words rather than mine.
//
// **IT IMPORTS NOTHING**, which is the entry requirement that lets a `.mjs` gate LOAD and RUN it.
// ---------------------------------------------------------------------------

/**
 * The three states a proposed mapping can be in. **`proposed` DELIVERS NOTHING** — her clause is that
 * nothing ships without her tap, and that is arithmetic here rather than a promise: only `approved`
 * is read by the resolver.
 */
export const MOSES_MAP_STATES = ["proposed", "approved", "skipped"];

export function mosesMapStateValid(state) {
  return typeof state === "string" && MOSES_MAP_STATES.includes(state);
}

/**
 * **THE THREE TAPS, DECLARED ONCE AND READ BY THE SCREEN AND BY THE DOOR.** A door taking a STATE off
 * the request body would let a posted field approve a mapping she skipped; it takes an ACTION key and
 * the law answers the state (R41 — the row is the fact, the payload is a spelling of it).
 */
export const MOSES_MAP_ACTIONS = [
  { key: "approve", label: "Approve", state: "approved" },
  { key: "edit", label: "Change the tag", state: "approved" },
  { key: "skip", label: "Skip", state: "skipped" },
];

export function mosesMapActionState(action) {
  if (typeof action !== "string") return "";
  const found = MOSES_MAP_ACTIONS.find((a) => a.key === action);
  return found ? found.state : "";
}

/**
 * >>> **THE TERM IS MATCHED AS A WHOLE PHRASE, NEVER AS A SUBSTRING, AND THIS HOUSE HAS PAID FOR
 * THAT THREE TIMES.** <<< ITEM 3's `ritual` inside `spi-ritual`, FUNNEL-4's `mars` inside *"a marsh
 * of feelings"*, and PDF-1's `all` inside *"my crystal ball reading"*. A term she approves for
 * *"hex"* must not fire on *"hexagram"*.
 *
 * Word boundaries are computed rather than regex-escaped into a pattern, because the terms come off a
 * database row and a member's approval is not a place to build a regex out of stored text.
 */
export function termMatches(text, term) {
  const hay = String(text ?? "").toLowerCase();
  const needle = String(term ?? "").trim().toLowerCase();
  // >>> THE SCAN IS A BOUNDED WALK, AND THIS ROUND'S OWN BATTERY IS WHY. <<<
  //
  // The first draft was `for (;;)` above an early `if (!needle) return false`. **M6 deleted that
  // guard and the fixture did not go red — IT SPUN FOREVER**, because `indexOf("", from)` clamps to
  // `hay.length` and never answers -1. A hang is one degree worse than a crash (R19): a crash is a
  // named red and a hang is indistinguishable from a slow machine, so the runner sits there.
  //
  // **AND FIXING IT BY ADDING A BOUND *BESIDE* THE GUARD WOULD HAVE BEEN AN R216 LINE**: with the walk
  // bounded, the guard can no longer change an answer for any input, and a line that cannot change an
  // answer is one no reader can trust. So there is ONE mechanism rather than two. An empty term walks
  // every position, is refused at each one because the character beside it is a word character, and
  // falls out of the loop — which is the same answer the guard gave, reached structurally.
  // **AND A BLANK TERM IS REFUSED EXPLICITLY, WHICH THE BOUND CANNOT DO FOR IT.** Driven: with an
  // empty ask AND an empty term the walk finds a position with no character on either side and
  // answers TRUE — so a blank row on her desk would bind its tag to an empty situation. The guard is
  // the only line that refuses it, which is what earns it its place (R216).
  if (!needle) return false;
  for (let from = 0; from + needle.length <= hay.length; from += 1) {
    const at = hay.indexOf(needle, from);
    if (at < 0) return false;
    const before = at === 0 ? "" : hay[at - 1];
    const after = at + needle.length >= hay.length ? "" : hay[at + needle.length];
    const isWord = (c) => c !== "" && /[a-z0-9]/.test(c);
    if (!isWord(before) && !isWord(after)) return true;
    from = at;
  }
  return false;
}

/**
 * **WHICH TAGS AN ASK EARNS FROM THE MAPPINGS SHE HAS APPROVED, AND ONLY THOSE.** A row in any other
 * state contributes NOTHING — her clause read as arithmetic. The order is the rows' own, so a later
 * round cannot change which tag leads by re-sorting here (that is `resolveMosesTables`' question).
 */
export function approvedTagsFor(text, rows) {
  const out = [];
  if (!Array.isArray(rows)) return out;
  for (const row of rows) {
    if (!row) continue;
    if (row.state !== "approved") continue;
    const tag = typeof row.tag === "string" ? row.tag.trim() : "";
    if (!tag) continue;
    if (!termMatches(text, row.term)) continue;
    if (!out.includes(tag)) out.push(tag);
  }
  return out;
}

/**
 * >>> **A MAPPING ONTO A TAG NO DELIVERABLE ENTRY CARRIES HEALS NOBODY, AND HER SCREEN SAYS SO
 * BEFORE SHE TAPS.** <<<
 *
 * Measured: **12 of her 19 entries have NO STAGED PLATE**, so MRM-6 fails them closed — which is
 * ITEM 12's own declared state (*"the Seventh Book honestly awaiting her plates"*). Every Seventh
 * Book table is in that twelve. So `court-justice`, `honor-fame`, `travel-safety`, `spirit-work` and
 * `wish` reach nothing a member can actually receive today, and approving a mapping onto one of them
 * changes a member's `no_match` into a different refusal rather than into a working.
 *
 * **Telling her that at the moment she taps is the difference between an approval list and a wish
 * list** (R17/R160: no silent caps). The roster is DERIVED from the entries rather than declared, so
 * the day she stages a plate the sentence changes by itself (R11/R31).
 */
export function deliverableTags(entries) {
  const out = [];
  if (!Array.isArray(entries)) return out;
  for (const e of entries) {
    if (!e || typeof e !== "object") continue;
    if (!e.plate) continue;
    for (const t of Array.isArray(e.tags) ? e.tags : []) {
      if (typeof t === "string" && t && !out.includes(t)) out.push(t);
    }
  }
  return out.sort();
}

/** What her screen is told about a proposal's reach. Three facts, never two (R18). */
export function mappingReach(tag, entries) {
  const all = [];
  const live = [];
  for (const e of Array.isArray(entries) ? entries : []) {
    if (!e || typeof e !== "object") continue;
    if (!(Array.isArray(e.tags) ? e.tags : []).includes(tag)) continue;
    all.push(e);
    if (e.plate) live.push(e);
  }
  if (!all.length) return { kind: "no-entry", entries: [], live: [] };
  if (!live.length) return { kind: "awaiting-plates", entries: all, live: [] };
  return { kind: "deliverable", entries: all, live };
}

export const MAPPING_REACH_COPY = {
  "no-entry": "No seal in either book carries this tag, so approving this changes nothing.",
  "awaiting-plates":
    "Her books carry a seal for this, and its plate is not staged yet — so approving this gives a "
    + "member an honest answer about a seal that exists rather than a working she can use.",
  deliverable: "This reaches a seal a member can receive today.",
};

export function mappingReachSentence(tag, entries) {
  return MAPPING_REACH_COPY[mappingReach(tag, entries).kind] || "";
}
